import { useCallback, useEffect, useMemo } from 'react';
import { merge } from 'ts-deepmerge';
import {
    ZHLPersonalizedNextStepExperience,
    ZHLPersonalizedNextStepState,
    useGetResourceCardModuleDataQuery,
} from 'src/services/graphql/generated/graphql';
import { NO_LEAD_AVAILABLE } from 'src/analytics/constants';
import { MaybeLoanID } from 'src/common/types';
import { getHomeLoansDashboardInteractionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardInteractionEvent';
import { getHomeLoansDashboardImpressionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardImpressionEvent';
import { trackEvent } from 'src/analytics/events/trackEvent';
import {
    HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS,
    HOME_LOANS_DASHBOARD_INTERACTION_EVENTS,
} from 'src/analytics/events/registeredEvents';
import { useUnauthorizedAccessGuard } from 'src/services/graphql/hooks/useUnauthorizedAccessGuard';
import { useFirstPartyEligibility } from 'src/experiences/MortgageDashboard/hooks/useFirstPartyEligibility/useFirstPartyEligibility';
import { ServerSideModuleData } from '../../GetServerSideProps/utils/getServerSideModuleData';
import { isUUID } from '../../../../utils/validators/isUUID';
import { useStartBlend } from '../../hooks/useStartBlend/useStartBlend';
import { useSendToFirstParty } from '../../hooks/useSendToFirstParty/useSendToFirstParty';

type ResourceCardState = {
    documentURL: string | null;
    downloadURL: string | null;
    trackPrimaryCTAClick: () => void;
    trackCloseButtonClick: () => void;
    trackOutsideModalClick: () => void;
    trackModalPrimaryCTAClick: () => void;
    sendToApplication: () => void;
    isLoading: boolean;
    status: ZHLPersonalizedNextStepState | null;
    isFirstPartyExperience: boolean;
};

const isServerSideDataSufficient = (data: Pick<ServerSideModuleData, 'ResourceCard'>) => {
    const { homeLoansDashboardByLoanId } = data.ResourceCard;
    const { personalizedNextSteps, currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { currentState: status, loanApplicationExperienceURL, currentExperience } = personalizedNextSteps ?? {};
    const { preApprovalLetterURL } = currentlySelectedLoan ?? {};
    const { ctaPreApprovalUrlWithDomain } = currentlySelectedLoan?.quickAppFeatures ?? {};
    const { documentURL, downloadURL } = preApprovalLetterURL ?? {};

    const isPreApprovalStatus =
        status === ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL ||
        status === ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS;

    if (isPreApprovalStatus) {
        if (currentExperience === ZHLPersonalizedNextStepExperience.FIRST_PARTY) {
            return !!ctaPreApprovalUrlWithDomain;
        }
        return !!documentURL && !!downloadURL;
    }

    return !!status && !!loanApplicationExperienceURL;
};

export const useResourceCardState = ({
    loanID,
    serverFetchedData,
}: {
    loanID: MaybeLoanID;
    serverFetchedData: Pick<ServerSideModuleData, 'ResourceCard'>;
}): ResourceCardState => {
    const { ResourceCard } = serverFetchedData;
    const isLoanIDInvalid = !isUUID(loanID);
    const { loading, data, error } = useGetResourceCardModuleDataQuery({
        variables: {
            id: loanID as string,
        },
        skip: isLoanIDInvalid || isServerSideDataSufficient(serverFetchedData),
    });
    useUnauthorizedAccessGuard(error);
    const mergedData = merge(data ?? {}, ResourceCard);
    const { homeLoansDashboardByLoanId } = mergedData;
    const { personalizedNextSteps, currentlySelectedLoan } = homeLoansDashboardByLoanId ?? {};
    const { preApprovalLetterURL, lead, currentlyLoggedInBorrower } = currentlySelectedLoan ?? {};
    const { id: borrower_id = null } = currentlyLoggedInBorrower ?? {};
    const {
        currentState: status = null,
        loanApplicationExperienceURL = null,
        currentExperience = null,
    } = personalizedNextSteps ?? {};
    const { ctaPreApprovalUrlWithDomain = null } = currentlySelectedLoan?.quickAppFeatures ?? {};
    const { id: lead_id = NO_LEAD_AVAILABLE } = lead ?? {};
    const { documentURL = null, downloadURL = null } = preApprovalLetterURL ?? {};

    const { isFirstPartyExperience } = useFirstPartyEligibility(status, currentExperience);

    const isPreApprovalStatus = useMemo(
        () =>
            status === ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL ||
            status === ZHLPersonalizedNextStepState.VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS,
        [status]
    );

    const { sendToFirstParty } = useSendToFirstParty(loanID, borrower_id);
    const { sendToBlend } = useStartBlend(loanID, borrower_id);

    const sendToApplication = useCallback(() => {
        if (!status || !currentExperience) {
            return;
        }

        if (isFirstPartyExperience) {
            if (isPreApprovalStatus) {
                sendToFirstParty(ctaPreApprovalUrlWithDomain);
            } else {
                sendToFirstParty(loanApplicationExperienceURL);
            }
        } else {
            sendToBlend();
        }
    }, [
        isFirstPartyExperience,
        sendToFirstParty,
        sendToBlend,
        loanApplicationExperienceURL,
        status,
        currentExperience,
        isPreApprovalStatus,
        ctaPreApprovalUrlWithDomain,
    ]);

    useEffect(() => {
        if (!status) {
            return;
        }

        trackEvent(
            getHomeLoansDashboardImpressionEvent({
                event: HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS.RESOURCE_CARD_MODULE_LOADED,
                journey_stage_cd: status,
                lead_id,
            })
        );
    }, [lead_id, status, loading]);

    const trackPrimaryCTAClick = useCallback(() => {
        if (!status) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: status,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.RESOURCE_CARD_PRIMARY_CTA_CLICK,
            })
        );
    }, [lead_id, status]);

    const trackCloseButtonClick = useCallback(() => {
        if (!status) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: status,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.RESOURCE_CARD_MODAL_CLOSE_CLICK,
            })
        );
    }, [lead_id, status]);

    const trackOutsideModalClick = useCallback(() => {
        if (!status) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: status,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.RESOURCE_CARD_MODAL_BACKDROP_CLICK,
            })
        );
    }, [lead_id, status]);

    const trackModalPrimaryCTAClick = useCallback(() => {
        if (!status) {
            return;
        }
        trackEvent(
            getHomeLoansDashboardInteractionEvent({
                lead_id,
                journey_stage_cd: status,
                event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.RESOURCE_CARD_MODAL_PRIMARY_CTA_CLICK,
            })
        );
    }, [lead_id, status]);

    const isLoading = loading || !status;

    return {
        trackPrimaryCTAClick,
        trackCloseButtonClick,
        trackOutsideModalClick,
        trackModalPrimaryCTAClick,
        sendToApplication,
        documentURL,
        downloadURL,
        isLoading,
        status,
        isFirstPartyExperience: currentExperience === ZHLPersonalizedNextStepExperience.FIRST_PARTY,
    };
};
